import { ApiResponse } from '@nf/types/api';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { useApiBackendInfo } from '@/hooks/query/use-api-backend-info';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/query-keys';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { ApiBackendInfo } from '@/hooks/query/athena/types';
import { GetWcPromotionLotteryCountByPoolIdDto } from './types';

const fetchWcPromotionLotteryCountByPoolId = async (
	apiBackendInfo: ApiBackendInfo,
	poolId?: string | null
): Promise<ApiResponse<GetWcPromotionLotteryCountByPoolIdDto>> => {
	const path =
		poolId && poolId.length > 0
			? `${apiBackendInfo.url}/WcPromotion/GetLotteryCountByPoolId?poolId=${encodeURIComponent(poolId)}`
			: `${apiBackendInfo.url}/WcPromotion/GetLotteryCountByPoolId`;
	const response = await fetchComposer.getWithBearer(path)(apiBackendInfo.token);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

/** 依獎池取得抽獎券數量；省略 poolId 時取得所有獎池。React Query 快取永不過期。 */
export const useWcPromotionLotteryCountByPoolId = (poolId?: string | null) => {
	const { enableWcPromotionLuckySpin, enableWcPromotionWeeklyLottery, enableWcPromotionAfterParty } = useLicSettings();
	const apiBackendInfo = useApiBackendInfo();
	const isWcPromotionEnabled = enableWcPromotionLuckySpin || enableWcPromotionWeeklyLottery || enableWcPromotionAfterParty;

	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [
			queryKeys.WC_PROMOTION_LOTTERY_COUNT_BY_POOL_ID,
			apiBackendInfo.url,
			apiBackendInfo.token,
			poolId ?? null
		],
		queryFn: () => fetchWcPromotionLotteryCountByPoolId(apiBackendInfo, poolId),
		staleTime: Infinity,
		cacheTime: Infinity,
		enabled: isWcPromotionEnabled && apiBackendInfo.isReady
	});

	return {
		data: data?.Data,
		isLoading,
		isError,
		error,
		refetch
	};
};
